import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  message,
  Alert,
  Divider,
  Upload,
  InputNumber,
  Switch,
  Space,
  DatePicker,
  Tag,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
  CalendarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

function CreateService(props) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);

  // API hooks
  const [createResponse, createService] = useApi(
    "admin/services/create",
    null,
    API_METHODS.POST,
    true
  );
  const [serviceTypesResponse, getServiceTypes] = useApi();

  // Fetch service types on mount
  useEffect(() => {
    getServiceTypes(null, API_METHODS.GET, "/admin/services/service-types");
  }, []);

  // Update service types when API responds
  useEffect(() => {
    if (serviceTypesResponse?.data && !serviceTypesResponse.isLoading) {
      setServiceTypes(serviceTypesResponse.data);
    }
  }, [serviceTypesResponse]);

  // After successful creation
  useEffect(() => {
    if (createResponse.data && !createResponse.isLoading) {
      messageApi.open({
        type: "success",
        content: `🎉 Service "${form.getFieldValue(
          "name"
        )}" created successfully!`,
        duration: 4,
      });
      form.resetFields();
      setFileList([]);
      setSelectedDates([]);
      setPreviewVisible(false);

      // Switch to list tab after success
      props.onServiceCreated && props.onServiceCreated();
    }
  }, [createResponse]);

  // Handle error response
  useEffect(() => {
    if (createResponse.error) {
      messageApi.open({
        type: "error",
        content: createResponse.error || "Failed to create service",
        duration: 4,
      });
    }
  }, [createResponse.error]);

  // Watch for service type changes
  const watchedValues = Form.useWatch([], form);
  const isServiceType5 = watchedValues?.service_type_id === 5;

  // Handle date selection for service type 5
  const handleDateChange = (date) => {
    if (date) {
      const dateString = date.format("YYYY-MM-DD");

      // Check if date is already selected
      if (!selectedDates.includes(dateString)) {
        setSelectedDates([...selectedDates, dateString]);
      } else {
        messageApi.warning("This date is already selected");
      }
    }
  };

  // Remove selected date
  const removeDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
  };

  // Validate dates for service type 5
  const validateDates = () => {
    if (isServiceType5 && selectedDates.length === 0) {
      messageApi.error(
        "Please select at least one future date for special occasion services"
      );
      return false;
    }
    return true;
  };

  // Handle submission
  const handleCreateService = () => {
    form.validateFields().then((values) => {
      // Validate dates for service type 5
      if (!validateDates()) {
        return;
      }

      // Check if image is uploaded
      if (fileList.length === 0) {
        messageApi.error("Please upload a service image");
        return;
      }

      const formData = new FormData();

      // Append form fields
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === "is_available" || key === "slot_required") {
            formData.append(key, values[key] ? 1 : 0);
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Append selected dates for service type 5
      if (isServiceType5 && selectedDates.length > 0) {
        selectedDates.forEach((date) => {
          formData.append("available_dates[]", date);
        });
      }

      // Append image file
      formData.append("service_image", fileList[0].originFileObj);

      createService(formData, API_METHODS.POST, "/admin/services/create", true);
    });
  };

  // Get price type color
  const getPriceTypeColor = (priceType) => {
    switch (priceType) {
      case "fixed":
        return "#52c41a";
      case "custom":
        return "#1890ff";
      case "minimum":
        return "#faad14";
      default:
        return "#666";
    }
  };

  // Handle file upload
  const handleUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // Toggle preview
  const handlePreview = () => {
    form
      .validateFields()
      .then(() => {
        setPreviewVisible(!previewVisible);
      })
      .catch(() => {
        messageApi.warning("Please fill all required fields to preview");
      });
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    return dayjs(dateString).format("MMM DD, YYYY");
  };

  return (
    <div>
      {contextHolder}
      <Row gutter={16}>
        {/* Create Component */}
        <Col xs={24} lg={previewVisible ? 12 : 24}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <PlusOutlined
                  style={{ marginRight: "8px", color: "#f97316" }}
                />
                Create New Service
              </div>
            }
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                price_type: "fixed",
                is_available: true,
                duration_minutes: 30,
                slot_required: true,
              }}
            >
              {/* Service Name */}
              <Form.Item
                label="Service Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter service name" },
                  { max: 100, message: "Name cannot exceed 100 characters" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter service name"
                  prefix={<TagOutlined style={{ color: "#f97316" }} />}
                  maxLength={100}
                  showCount
                />
              </Form.Item>

              {/* Service Type */}
              <Form.Item
                label="Service Type"
                name="service_type_id"
                rules={[
                  { required: true, message: "Please select service type" },
                ]}
              >
                <Select
                  size="large"
                  placeholder="Select service type"
                  loading={serviceTypesResponse.isLoading}
                  onChange={() => {
                    // Clear selected dates when service type changes
                    setSelectedDates([]);
                  }}
                >
                  {serviceTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px" }}>
                          {type.icon || "🕉️"}
                        </span>
                        {type.name}
                        {type.id === 5 && (
                          <Tag color="orange" style={{ marginLeft: "8px" }}>
                            Special Occasion
                          </Tag>
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Special Occasion Date Selection - Only for Service Type 5 */}
              {isServiceType5 && (
                <Card
                  size="small"
                  title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <CalendarOutlined
                        style={{ marginRight: "8px", color: "#f97316" }}
                      />
                      Special Occasion Dates *
                    </div>
                  }
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#fff7e6",
                    border: "2px solid #ffa940",
                  }}
                >
                  <Alert
                    message="Special Occasion Service"
                    description="This service type requires specific dates when it will be available. Please select all future dates when this service can be booked."
                    type="info"
                    showIcon
                    style={{ marginBottom: "16px" }}
                  />

                  {/* Date Picker */}
                  <Form.Item
                    label="Select Available Dates"
                    style={{ marginBottom: "16px" }}
                  >
                    <DatePicker
                      size="large"
                      placeholder="Click to add a date"
                      style={{ width: "100%" }}
                      disabledDate={(current) => {
                        // Disable past dates
                        return current && current < dayjs().startOf("day");
                      }}
                      onChange={handleDateChange}
                      value={null} // Always show empty after selection
                    />
                  </Form.Item>

                  {/* Selected Dates Display */}
                  {selectedDates.length > 0 && (
                    <div>
                      <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                        Selected Dates ({selectedDates.length}):
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {selectedDates.map((date, index) => (
                          <Tag
                            key={index}
                            closable
                            onClose={() => removeDate(date)}
                            color="orange"
                            style={{
                              padding: "4px 8px",
                              fontSize: "12px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <CalendarOutlined style={{ marginRight: "4px" }} />
                            {formatDateForDisplay(date)}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDates.length === 0 && (
                    <Alert
                      message="No dates selected"
                      description="Please select at least one future date for this special occasion service."
                      type="warning"
                      showIcon
                    />
                  )}
                </Card>
              )}

              {/* Description */}
              <Form.Item
                label="Service Description"
                name="description"
                rules={[
                  {
                    max: 500,
                    message: "Description cannot exceed 500 characters",
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Enter service description"
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              {/* Price Type */}
              <Form.Item
                label="Price Type"
                name="price_type"
                rules={[
                  { required: true, message: "Please select price type" },
                ]}
              >
                <Select size="large" placeholder="Select price type">
                  <Option value="fixed">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#52c41a",
                          marginRight: "8px",
                        }}
                      />
                      Fixed Price
                    </div>
                  </Option>
                  <Option value="custom">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#1890ff",
                          marginRight: "8px",
                        }}
                      />
                      Custom Amount (Donations)
                    </div>
                  </Option>
                  <Option value="minimum">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#faad14",
                          marginRight: "8px",
                        }}
                      />
                      Minimum Amount
                    </div>
                  </Option>
                </Select>
              </Form.Item>

              {/* Price Fields */}
              <Row gutter={16}>
                {/* Fixed Price */}
                {watchedValues?.price_type === "fixed" && (
                  <Col span={24}>
                    <Form.Item
                      label="Service Price (₹)"
                      name="price"
                      rules={[
                        {
                          required: true,
                          message: "Please enter service price",
                        },
                      ]}
                    >
                      <InputNumber
                        size="large"
                        placeholder="Enter price"
                        prefix="₹"
                        min={1}
                        max={100000}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                )}

                {/* Custom Price */}
                {watchedValues?.price_type === "custom" && (
                  <Col span={12}>
                    <Form.Item
                      label="Maximum Amount (₹) - Optional"
                      name="max_price"
                    >
                      <InputNumber
                        size="large"
                        placeholder="Enter max amount"
                        prefix="₹"
                        min={1}
                        max={100000}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                )}

                {/* Minimum Price */}
                {watchedValues?.price_type === "minimum" && (
                  <React.Fragment>
                    <Col span={12}>
                      <Form.Item
                        label="Minimum Amount (₹)"
                        name="min_price"
                        rules={[
                          {
                            required: true,
                            message: "Please enter minimum amount",
                          },
                        ]}
                      >
                        <InputNumber
                          size="large"
                          placeholder="Enter min amount"
                          prefix="₹"
                          min={1}
                          max={100000}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Maximum Amount (₹) - Optional"
                        name="max_price"
                      >
                        <InputNumber
                          size="large"
                          placeholder="Enter max amount"
                          prefix="₹"
                          min={1}
                          max={100000}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </React.Fragment>
                )}
              </Row>

              {/* Duration */}
              <Form.Item
                label="Service Duration (minutes)"
                name="duration_minutes"
                rules={[
                  { required: true, message: "Please enter service duration" },
                ]}
              >
                <InputNumber
                  size="large"
                  placeholder="Enter duration in minutes"
                  prefix={<ClockCircleOutlined />}
                  min={5}
                  max={480}
                  style={{ width: "100%" }}
                />
              </Form.Item>

              {/* Time Slot Requirement */}
              <Form.Item
                label="Time Slot"
                name="slot_required"
                valuePropName="checked"
                extra="When required, the devotee must pick one of the fixed puja slots (5:00 AM / 7:30 AM / 10:00 AM). When optional, the slot picker is hidden and the puja happens as per temple schedule."
              >
                <Switch
                  checkedChildren="Slot required"
                  unCheckedChildren="Slot optional"
                />
              </Form.Item>

              {/* Service Image */}
              <Form.Item label="Service Image" name="service_image">
                <Upload
                  listType="picture"
                  fileList={fileList}
                  onChange={handleUpload}
                  beforeUpload={() => false}
                  accept="image/*"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />} size="large">
                    Upload Service Image
                  </Button>
                </Upload>
              </Form.Item>

              {/* Availability */}
              <Form.Item
                label="Service Availability"
                name="is_available"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Available"
                  unCheckedChildren="Unavailable"
                />
              </Form.Item>

              {/* Action Buttons */}
              <Form.Item>
                <Space size="middle">
                  <Button
                    type="default"
                    size="large"
                    onClick={handlePreview}
                    icon={<ExclamationCircleOutlined />}
                  >
                    {previewVisible ? "Hide Preview" : "Preview"}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleCreateService}
                    loading={createResponse.isLoading}
                    icon={<SaveOutlined />}
                    style={{
                      background: "linear-gradient(45deg, #f97316, #dc2626)",
                      border: "none",
                    }}
                  >
                    Create Service
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            {/* Service Type Info */}
            {watchedValues?.service_type_id && (
              <Alert
                message={
                  isServiceType5
                    ? "Special Occasion Service"
                    : "Service Type Selected"
                }
                description={
                  isServiceType5
                    ? `This service will only be available on the selected dates: ${selectedDates.length} date(s) chosen`
                    : serviceTypes.find(
                        (type) => type.id === watchedValues.service_type_id
                      )?.description ||
                      "Service will be created under the selected category"
                }
                type={isServiceType5 ? "warning" : "info"}
                showIcon
                style={{ marginTop: "16px" }}
              />
            )}
          </Card>
        </Col>

        {/* Preview Panel */}
        {previewVisible && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <TagOutlined
                    style={{ marginRight: "8px", color: "#1890ff" }}
                  />
                  Service Preview
                </div>
              }
            >
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: "8px",
                  padding: "16px",
                  backgroundColor: "#fafafa",
                  minHeight: "200px",
                }}
              >
                {/* Service Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#333",
                    }}
                  >
                    {watchedValues?.name || "Service Name"}
                  </div>
                  <div
                    style={{
                      marginLeft: "auto",
                      padding: "4px 8px",
                      backgroundColor: getPriceTypeColor(
                        watchedValues?.price_type
                      ),
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {watchedValues?.price_type || "fixed"}
                  </div>
                </div>

                {/* Service Type */}
                {watchedValues?.service_type_id && (
                  <div style={{ marginBottom: "8px" }}>
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      Category:{" "}
                      {
                        serviceTypes.find(
                          (type) => type.id === watchedValues.service_type_id
                        )?.name
                      }
                      {isServiceType5 && (
                        <Tag color="orange" style={{ marginLeft: "8px" }}>
                          Special Occasion
                        </Tag>
                      )}
                    </span>
                  </div>
                )}

                {/* Special Occasion Dates */}
                {isServiceType5 && selectedDates.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#f97316",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                    >
                      🎉 Special Dates:
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {selectedDates
                        .map((date) => formatDateForDisplay(date))
                        .join(", ")}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    lineHeight: "1.6",
                    marginBottom: "12px",
                  }}
                >
                  {watchedValues?.description ||
                    "Service description will appear here..."}
                </div>

                {/* Price Display */}
                <div style={{ marginBottom: "12px" }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#f97316",
                    }}
                  >
                    <DollarOutlined style={{ marginRight: "4px" }} />
                    {watchedValues?.price_type === "fixed" &&
                      watchedValues?.price &&
                      `₹${watchedValues.price}`}
                    {watchedValues?.price_type === "custom" && "Any Amount"}
                    {watchedValues?.price_type === "minimum" &&
                      watchedValues?.min_price &&
                      `Min ₹${watchedValues.min_price}`}
                    {!watchedValues?.price &&
                      !watchedValues?.min_price &&
                      "Price not set"}
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom: "12px" }}>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    <ClockCircleOutlined style={{ marginRight: "4px" }} />
                    Duration: {watchedValues?.duration_minutes || 30} minutes
                  </span>
                </div>

                <Divider style={{ margin: "12px 0" }} />

                {/* Footer */}
                <div
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    textAlign: "center",
                  }}
                >
                  Service will be{" "}
                  {watchedValues?.is_available ? "available" : "unavailable"}{" "}
                  for booking
                  {isServiceType5 &&
                    selectedDates.length > 0 &&
                    ` on ${selectedDates.length} selected date(s)`}
                </div>
              </div>

              {/* Preview Info */}
              <Alert
                message="Preview Information"
                description={
                  isServiceType5
                    ? "This special occasion service will only be bookable on the selected dates. Users will see the available dates on the service card."
                    : "This is how your service will appear to users on the booking platform"
                }
                type="info"
                showIcon
                style={{ marginTop: "16px" }}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}

export default CreateService;
